/**
 * **房主选举 —— 纯函数，所有人独立算出同一个人。**
 *
 * 这一节沿用 `old_repo` 的 `#tryEvaluateFinalLock`（`S3Session.ts:458-506`）
 * 里**唯一**属于共识的那一步。它的第一步（内容包哈希对齐）是**校验**不是共识，
 * 所以放在 `commonPacks()` 里分开。
 *
 * ## ⚠️ 为什么这里吃的是"矩阵"而不是"一维"
 *
 * `old_repo` 把"每个 peer 上报自己**对所有目标**的 rtts"加起来求总分。
 * 这个做法是对的，而"只测到自己到房主的延迟"是错的 ——
 * 后者选出来的"最快"是**相对于房主**最快的，不是相对于**整间房**最快的。
 *
 * 大厅阶段用一维没问题（那时所有人都只跟创建者通信，见
 * `src/ui/room.ts` 的 `electGameHost()`）；但"进入游戏后由网络最快者担任"
 * 用的是矩阵。两个版本都留着，各有各的适用期。
 *
 * ## 三条性质（`architecture.md` §6.4 要求选举必须是纯函数）
 *
 * 1. **不看谁创建了房间** —— 创建者可能就是那个网络最差的人
 * 2. **没有数据的人算最慢** —— 一个还没测出延迟的人不该当权威
 * 3. **平局按 id 字典序** —— 否则会出现两个人都认为自己是房主
 *
 * 第 3 条是**最容易漏、后果最严重**的一条：没有它，`Math.min` 或
 * `for` 循环的遍历顺序会决定房主，而不同 peer 的 Map 插入顺序是不一样的。
 */

import type { RttRow } from './heartbeat';

/** `matrix[reporter][target] = rttMs`。`reporter` 只需要包含那些上报过的人 */
export type RttMatrix = Readonly<Record<string, RttRow>>;

export interface ElectionInput {
  /** 有资格参选的名册。**必须是所有 peer 都同意的那一份**（见 `consensusRoster`） */
  readonly players: readonly string[];
  readonly matrix: RttMatrix;
}

/**
 * 选出对局房主。
 *
 * `score(X) = Σ_reporter matrix[reporter][X]`，取最小；平局取字典序最小的 id。
 *
 * 缺格有两种，处理方式**刻意不对称**：
 *
 * | 情况 | 处理 | 为什么 |
 * | --- | --- | --- |
 * | **某一格**是 `NaN` / `Infinity` / 没有 | **跳过那一格** | 一条坏链路不该让某人的总分变成无穷大 —— 那样所有人并列，选举退化成字典序，反而更糟 |
 * | **整个候选人**一格都没被测到 | 总分 = `Infinity`（最差） | ⚠️ 不能让它算 0！0 是**最好**的分数，于是"还没测出延迟的人"会当选权威 —— 恰好和设计意图相反。这条是写完测试才发现的 |
 *
 * 名册为空时返回 `null`。
 */
export function electHost(input: ElectionInput): string | null {
  const { players, matrix } = input;
  if (players.length === 0) return null;

  let best: string | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const candidate of players) {
    let score = 0;
    let measured = 0;
    for (const reporter of Object.keys(matrix)) {
      const row = matrix[reporter];
      const value = row?.[candidate];
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      score += value;
      measured += 1;
    }
    // 一格都没测到 = 最慢。见上面那张表
    if (measured === 0) score = Number.POSITIVE_INFINITY;

    if (score < bestScore || (score === bestScore && best !== null && candidate < best)) {
      best = candidate;
      bestScore = score;
    }
  }

  return best;
}

/**
 * 名册共识：按"排序后的签名"投票，取众数。
 *
 * 为什么需要它：每个人的 `peers()` 是**本地视野**。有人可能还没看到刚进来的
 * 那个人，于是"A 看到 [a,b,c]、B 看到 [a,b]"这种分歧是正常的。
 * 直接拿自己的视野去选举，不同的人会在**不同的候选集合**里选，
 * 于是即使选举函数本身是纯的，结果也可能不一致。
 *
 * 平局时取**签名较小**的那个（字符串比较），所以这个函数也是确定的。
 */
export function consensusRoster(rows: readonly (readonly string[])[]): readonly string[] {
  const votes = new Map<string, { list: readonly string[]; count: number }>();
  for (const row of rows) {
    const list = [...row].sort();
    const signature = list.join(',');
    const existing = votes.get(signature);
    if (existing === undefined) votes.set(signature, { list, count: 1 });
    else existing.count += 1;
  }

  let winner: { list: readonly string[]; count: number } | null = null;
  let winnerSignature = '';
  for (const [signature, entry] of votes) {
    if (
      winner === null ||
      entry.count > winner.count ||
      (entry.count === winner.count && signature < winnerSignature)
    ) {
      winner = entry;
      winnerSignature = signature;
    }
  }
  return winner?.list ?? [];
}

/**
 * 内容包（或"插件"）哈希取交集：**所有人都有的那些**。
 *
 * 从 `old_repo` 的 `getRoomPluginHashes()` 搬过来，但换了对象 ——
 * 那边是插件，这边是**内容包**（`core/pack.ts`）。
 * 语义不变：只有全员都有的包才能进这一局。
 *
 * `localHashes` 的第一项是"我自己的"，顺序不影响结果（结果按 `localHashes` 的顺序）。
 */
export function commonPacks(localHashes: readonly string[], remoteRows: readonly (readonly string[])[]): readonly string[] {
  if (remoteRows.length === 0) return [...localHashes];
  return localHashes.filter((hash) => remoteRows.every((row) => row.includes(hash)));
}

/**
 * 本地可用、但房间里**不是所有人都有**的包，附带"谁没有"。
 *
 * 这是给界面用的：直接告诉玩家"<小明> 没有这张扩展包"，
 * 比一句"内容包不一致"有用得多。
 */
export function unavailablePacks(
  localHashes: readonly string[],
  rows: ReadonlyMap<string, readonly string[]>,
): { readonly hash: string; readonly missing: readonly string[] }[] {
  const common = new Set(commonPacks(localHashes, [...rows.values()]));
  const out: { hash: string; missing: string[] }[] = [];
  for (const hash of localHashes) {
    if (common.has(hash)) continue;
    const missing: string[] = [];
    for (const [peerId, row] of rows) {
      if (!row.includes(hash)) missing.push(peerId);
    }
    out.push({ hash, missing });
  }
  return out;
}
